import {Pagination, Table, Tag, Empty, Spin} from "antd";
import ProductAction from "./ProductAction";
import {useContext, useState} from "react";
import {AppContext} from "~/context/AppProvider";
import {formatCurrency, handleRequest, apiError} from "~/utils";
import {productApi} from "~/api";
import '../../style/ProductTable.css';
import {Button, Icon} from "../../../../components";

function ProductTable({items = [], pagination, onPaginationChange, onEdit, onDelete, can}) {

	const {tableHeight, appData} = useContext(AppContext);

	const [expandedKeys, setExpandedKeys] = useState([]);

	const [variationsLoading, setVariationsLoading] = useState({});

	const [variationsData, setVariationsData] = useState({});

	// Format price based on unit
	const formatPriceWithUnit = (price, unit) => {
		const formattedPrice = formatCurrency(price);
		if (unit === 'kg')
		{
			return `${formattedPrice} / 0.1kg`;
		}

		unit = appData['units'][unit]?.label || unit;

		return formattedPrice + ` / ${unit}`;
	};

	const handleExpandVariations = async (parentId, hasVariation) => {
		if (hasVariation !== 1) return;

		// Toggle expand
		const isExpanded = expandedKeys.includes(parentId);
		if (isExpanded) {
			setExpandedKeys(expandedKeys.filter(key => key !== parentId));
			return;
		}

		// Load variations if not already loaded
		if (!variationsData[parentId]) {
			setVariationsLoading(prev => ({...prev, [parentId]: true}));
			let [error, response] = await handleRequest(productApi.variations(parentId));
			setVariationsLoading(prev => ({...prev, [parentId]: false}));

			if (!apiError('Lấy biến thể thất bại', error, response)) {
				setVariationsData(prev => ({
					...prev,
					[parentId]: response.data || []
				}));
			}
		}

		// Close all other expanded rows and open only this one
		setExpandedKeys([parentId]);
	};

	let columns = [
		{ title: 'Mã SP', dataIndex: 'code', key: "code", width: '150px', render: (_, item) => (
			<div onClick={() => handleExpandVariations(item.id, item.hasVariation)} style={{cursor: item.hasVariation === 1 ? 'pointer' : 'default', padding: '8px'}}>
				{item.hasVariation === 1 ? (
					<span>
						<strong>({item.variationCount || 0})</strong> Mã hàng
					</span>
				) : (
					<b>{item?.code}</b>
				)}
			</div>
		)},
		{ title: 'Tên sản phẩm', dataIndex: 'title', key: "title", width: '250px', render: (_, item) => (
			<div onClick={() => handleExpandVariations(item.id, item.hasVariation)} style={{cursor: item.hasVariation === 1 ? 'pointer' : 'default', padding: '8px'}}>
				<p><b>{item.title}</b></p>
			</div>
		)},
		{ title: 'Giá bán', dataIndex: 'price', key: "price", width: '150px', render: (_, item) => (
			<div onClick={() => handleExpandVariations(item.id, item.hasVariation)} style={{cursor: item.hasVariation === 1 ? 'pointer' : 'default', padding: '8px'}}>
				<p>{formatPriceWithUnit(item.price, item.unit)}</p>
			</div>
		)},
		{ title: 'Thuế (%)', dataIndex: 'vat', key: "vat", width: '100px', render: (_, item) => (
				<div onClick={() => handleExpandVariations(item.id, item.hasVariation)} style={{cursor: item.hasVariation === 1 ? 'pointer' : 'default', padding: '8px'}}>
					<p>{item.vat}%</p>
				</div>
			)},
		{ title: 'Giá sau thuế', dataIndex: 'price_after_tax', key: "price_after_tax", width: '170px', render: (_, item) => {
			const priceAfterTax = item.price * (1 + (item.vat || 0) / 100);
			return (
				<div onClick={() => handleExpandVariations(item.id, item.hasVariation)} style={{cursor: item.hasVariation === 1 ? 'pointer' : 'default', padding: '8px'}}>
					<p style={{color: '#1890ff', fontWeight: '500'}}>{formatPriceWithUnit(priceAfterTax, item.unit)}</p>
				</div>
			);
		}},
		{ title: 'Tồn kho', dataIndex: 'stock', key: "stock", width: '150px'},
		{ title: '#', dataIndex: 'action', key: "action", width: '150px', render: (_, item) => (
			<ProductAction key={item.id}
						   item={item}
						   onEdit={onEdit}
						   onDelete={onDelete}
						   can={can}
			/>
		)},
	];

	const dataSource = items.map(row => ({
		key: row.id,
		...row
	}));

	// Bảng biến thể con
	const variationColumns = [
		{ title: 'Mã', dataIndex: 'code', key: "var_code", width: '100px', render: (_, item) => (
			<b>{item.code}</b>
		)},
		{ title: 'Tên biến thể', dataIndex: 'attribute_label', key: "attribute_label", width: '200px', render: (_, item) => (
			<>
				<p>{ item.title }</p>
				<span className={'fw-bold color-blue'}>{item.attribute_label}</span>
			</>
		)},
		{ title: 'Giá bán', dataIndex: 'price', key: "var_price", width: '150px', render: (_, item) => (
			<span>{formatPriceWithUnit(item.price, item.unit)}</span>
		)},
		{ title: 'Thuế (%)', dataIndex: 'vat', key: "var_vat", width: '100px', render: (_, item) => (
			<span>{item.vat}%</span>
		)},
		{ title: 'Giá sau thuế', key: "var_price_after_tax", width: '170px', render: (_, item) => {
			const priceAfterTax = item.price * (1 + (item.vat || 0) / 100);
			return <span style={{color: '#1890ff', fontWeight: '500'}}>{formatPriceWithUnit(priceAfterTax, item.unit)}</span>;
		}},
		{ title: 'Tồn kho', dataIndex: 'stock', key: "stock", width: '150px'},
		{ title: 'Hành động', key: "var_action", width: '100px', render: (_, item) => (
			<div style={{display: 'flex', gap: '8px'}}>
				<Button small background red onClick={() => onDelete(item)}>{Icon.delete}</Button>
			</div>
		)},
	];

	return (
		<div className="table-wrapper">
			<div className="table-box">
				<Table
					columns={columns}
					dataSource={dataSource}
					scroll={{ y:tableHeight, x: 'max-content' }}
					pagination={false}
					rowClassName={(record) => expandedKeys.includes(record.id) ? 'product-row-expanded' : ''}
					expandable={{
						expandedRowKeys: expandedKeys,
						onExpandedRowsChange: setExpandedKeys,
						expandedRowRender: (record) => {
							if (record.hasVariation !== 1) return null;

							const variations = variationsData[record.id] || [];
							const isLoading = variationsLoading[record.id];

							return (
								<div className="variations-wrapper">
									<Spin spinning={isLoading}>
										{variations.length === 0 ? (
											<Empty description="Chưa có biến thể" />
										) : (
											<Table
												columns={variationColumns}
												dataSource={variations.map(v => ({key: v.id, ...v}))}
												pagination={false}
												size="small"
											/>
										)}
									</Spin>
								</div>
							);
						},
					}}
				/>
			</div>
			<Pagination current={pagination?.page} defaultCurrent={1} defaultPageSize={20} pageSize={pagination?.limit} total={pagination?.total} onChange={onPaginationChange}  showSizeChanger={false} />
		</div>
	)
}

export default ProductTable;
